import { ParseError } from 'eventsource-parser';
import { logWarning } from 'features/logging/logError';
import { logWarn } from 'features/logging/logWarn';
import { trackingWrapper } from 'features/tracking/wrapper/TrackingWrapper';
import { authService } from 'services/auth/AuthService';
import { makeBackofficeUrl } from 'services/backofficeIntegration/http/backofficeUrls';
import { ConversationMessageDto } from 'services/backofficeIntegration/http/dtos/ConversationMessageDto';
import { processEventStream } from 'services/backofficeIntegration/http/processEventStream';
import { GAEvents } from 'services/tracking/GAEvents';

export type PartialMessageDto = Pick<ConversationMessageDto, 'id' | 'source' | 'created_at'>;

const EventType = {
  start: 'start',
  update: 'update',
  end: 'end',
  error: 'error'
} as const;

type NewMessageDto = ConversationMessageDto;
type Chunk = string;
type EventDto = StartEvent | UpdateEvent | EndEvent | ErrorEvent;
type StartEvent = { type: typeof EventType.start; message: PartialMessageDto };
type UpdateEvent = { type: typeof EventType.update; delta: { text: string } };
type EndEvent = { type: typeof EventType.end };
type ErrorEvent = { type: typeof EventType.error; message: string };
export type MessageInput = { projectId: string; conversationId: string; message: NewMessageDto };

class StreamError extends Error {
  event: ErrorEvent;

  constructor(event: ErrorEvent) {
    super('Stream error');
    this.event = event;
  }
}

const tryGetErrorCodeFromParserError = (parserError: ParseError) => {
  /*
    {
      "message":"ERROR_CONVERSATION_RESPONSE_GENERATION_FAILED",
      "details":"Latest message is too long to fit within token limit"
    }
   */

  if (!parserError.line) {
    return null;
  }

  const jsonString = parserError.line;
  try {
    const json = JSON.parse(jsonString) as { message?: unknown };
    if (typeof json.message === 'string') {
      return json.message;
    }
  } catch {
    return null;
  }

  return null;
};

function sendMessage(props: MessageInput) {
  const { projectId, conversationId, message } = props;

  trackingWrapper.track('generateTexts', {
    url: window.location.pathname,
    location: 'ai_writer_chat',
    input: props
  });

  return fetch(
    makeBackofficeUrl(
      `/projects/ai-writer/${projectId}/conversations/${conversationId}/stream-messages`
    ),
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...authService.getAuthorizationHeaders()
      },
      method: 'PUT',
      body: JSON.stringify(message)
    }
  );
}

async function handleStream({
  response,
  onUpdate,
  onStart,
  onEnd,
  onErrorCode
}: {
  response: Response;
  onUpdate: (chunk: Chunk) => void;
  onStart: (message: PartialMessageDto) => void;
  onEnd: () => void;
  onErrorCode: ErrorCodeHandler;
}) {
  return processEventStream({
    response,
    onParserError: async parserError => {
      // Note: Our streaming implementation contain's a bunch of newlines, which is usually not expected.
      //       The library is therefore also throwing errors for those.
      if (!parserError || !parserError.line || parserError.line.trim().length === 0) {
        return;
      }

      logWarning('ChatFlash sendMessage parser error', 'chat', { parserError });

      const errorCode = tryGetErrorCodeFromParserError(parserError);
      if (errorCode) {
        onErrorCode(errorCode);
        return;
      }

      throw new StreamError({ type: EventType.error, message: 'Parser error' });
    },
    onEvent: context => {
      const { event, cancel } = context;

      const dataString = event.data;
      let data: EventDto;
      try {
        data = JSON.parse(dataString) as EventDto;
      } catch (error) {
        // In case some events are not valid JSON, skip them
        logWarn('Failed to parse event data', { dataString, error });
        return;
      }

      switch (data.type) {
        case EventType.start: {
          const message = data.message;
          onStart(message);
          return;
        }
        case EventType.update: {
          const text = data.delta.text;
          onUpdate(text);
          return;
        }
        case EventType.end: {
          onEnd();
          cancel();
          return;
        }
        case EventType.error: {
          cancel();
          throw new StreamError(data);
        }
      }
    }
  });
}

export type ErrorCodeHandler = (errorCode?: string) => void;

export const httpSendMessage = {
  callStreamEndpoint: async (props: {
    projectId: string;
    conversationId: string;
    message: NewMessageDto;
    onUpdate: (chunk: Chunk) => void;
    onStart: (message: PartialMessageDto) => void;
    onErrorCode: ErrorCodeHandler;
    onEnd: () => void;
  }) => {
    const { onUpdate, onStart, onErrorCode, onEnd } = props;

    const response = await sendMessage(props);

    if (response && !response.ok) {
      const data = await response.json();
      if (data.message === 'ERROR_NO_GPT4_BALANCE') {
        GAEvents.gpt4LimitationViewed();
      }

      if (data.message) {
        onErrorCode(data.message);
      }
    }

    await handleStream({ response, onUpdate, onStart, onEnd, onErrorCode });
  }
};
